// ---------------------------------------------------------------------------
// THE "CHECK YOUR INBOX" STATE — shown after a sign-up email goes out.
//
// WHY THIS EXISTS (Aug 20, measured): two real users on launch morning tapped
// "Sign up", saw only a toast that vanished, assumed nothing happened while
// their email crawled toward Yahoo/Gmail, and tapped again. Leilani tapped
// FIVE times in 62 seconds. What neither of them could know: every new
// request quietly invalidates the previous link — only the NEWEST email
// works. She then clicked four dead links and gave up. Kathleen double-tapped
// and never clicked anything.
//
// The form re-arming itself after success was the bug. This component
// replaces it with a persistent state that (1) says the email is on its way
// and may take a minute, (2) says in plain words that only the newest email
// works, and (3) rate-limits "send it again" behind a 60-second countdown —
// matching the human rhythm of "check inbox, wait, sigh, retry" instead of
// the 9-second double-tap the logs recorded.
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { Loader2, MailCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const RESEND_COOLDOWN_S = 60;

interface Props {
  email: string;
  /** Same HTTPS redirect the original signUp used — resends must match. */
  emailRedirectTo: string;
  /** Optional: go back to the form (wrong address typed). */
  onChangeEmail?: () => void;
}

export const ConfirmEmailSent = ({ email, emailRedirectTo, onChangeEmail }: Props) => {
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_S);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const resend = async () => {
    if (resending || cooldown > 0) return;
    setResending(true);
    try {
      // auth.resend reissues the confirmation for an EXISTING pending
      // sign-up — the right call here, not a second signUp().
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo },
      });
      if (error) throw error;
      setResent(true);
      setCooldown(RESEND_COOLDOWN_S);
      toast.success("New link sent", {
        description: "Only this newest email will work now.",
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not resend just now");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 text-center space-y-3">
      <MailCheck className="w-8 h-8 text-primary mx-auto" aria-hidden />
      <h2 className="font-semibold text-foreground">Check your inbox</h2>
      <p className="text-sm text-muted-foreground leading-relaxed">
        We sent a confirmation link to <span className="font-medium text-foreground">{email}</span>.
        It can take a minute or two to arrive — worth a peek in spam, too.
      </p>
      {/* The sentence that would have saved Leilani's morning. */}
      <p className="text-xs text-muted-foreground leading-relaxed">
        {resent
          ? "Use the newest email — requesting a new link retires the older ones."
          : "If you ask for another link, only the newest email will work."}
      </p>
      <div className="flex flex-col items-center gap-2 pt-1">
        <button
          type="button"
          onClick={resend}
          disabled={cooldown > 0 || resending}
          className="inline-flex items-center gap-2 text-sm font-medium text-primary disabled:text-muted-foreground/60 min-h-11 px-4 py-2 rounded-full border border-border disabled:border-border/60 bg-background touch-manipulation"
        >
          {resending && <Loader2 className="w-4 h-4 animate-spin" aria-hidden />}
          {cooldown > 0 ? `Send it again (${cooldown}s)` : "Send it again"}
        </button>
        {onChangeEmail && (
          <button
            type="button"
            onClick={onChangeEmail}
            className="text-xs text-muted-foreground underline underline-offset-2 min-h-8 touch-manipulation"
          >
            Wrong address? Go back
          </button>
        )}
      </div>
    </div>
  );
};
